import {JobStopType} from "../../utils/stops/StopUtils";
import {RowData} from "./types/types";

const SECONDS_PER_HOUR = 3600;
const SECONDS_PER_MINUTE = 60;
const MILLIS_PER_MINUTE = 60_000;
const CENTS_PRECISION = 2;
/** Decimal places a rate may carry — matches the dialog's minorStepSize of 0.001. */
export const RATE_PRECISION = 3;

/** The five optional rates the dispatcher can enter, keyed by the metric they price. */
export type RateKey = "perMile" | "perHour" | "perDriver" | "perDeliveryStop" | "perPickupStop";

/**
 * Rates are held as the RAW strings the user typed, never as numbers.
 *
 * Blueprint's NumericInput is fully controlled — getDerivedStateFromProps echoes props.value
 * back into its internal state on every render. Storing a number would round-trip "5." to "5",
 * swallow the trailing dot, and make "5.55" impossible to type. Keeping the string makes the
 * partial input a display concern and leaves parsing to parseRate.
 */
export type RouteCostRates = Record<RateKey, string>;

export interface RouteCostTotals {
  totalMiles: number;
  /**
   * Elapsed on-duty seconds — driver start to route end, **including dwell at stops and time
   * spent waiting for early time windows** (PO confirmed 2026-07-30, reversing the original
   * driving-only reading). Summed from RowData.duration, each route truncated to whole minutes
   * first so the total equals the sum of the per-route durations shown in the grid.
   *
   * That truncation discards up to 59s per route, so the $/hour line can understate by a few
   * cents on a multi-route plan. Accepted deliberately: a total that disagrees with the visible
   * per-route values reads as a bug, and the drift is far below the precision of the rates.
   *
   */
  totalTransitSeconds: number;
  /** Route count, not distinct drivers — one driver assigned three routes counts as 3. */
  totalRoutes: number;
  /** "Both" stops are counted here, not as pickups. */
  totalDeliveryStops: number;
  totalPickupStops: number;
}

export const EMPTY_RATES: RouteCostRates = {
  perMile: "",
  perHour: "",
  perDriver: "",
  perDeliveryStop: "",
  perPickupStop: ""
};

const roundTo = (value: number, precision: number) => {
  const factor = 10 ** precision;
  return Math.round(value * factor) / factor;
};

/**
 * Parse a raw rate string into a usable multiplier.
 *
 * Deliberately forgiving of partial input: parseFloat("5.") is 5, so a half-typed decimal
 * already prices correctly. Everything non-numeric or non-positive ("", ".", "-", "0")
 * contributes nothing, which is what makes the inputs genuinely optional.
 */
export const parseRate = (raw: string | undefined): number => {
  const parsed = parseFloat(raw ?? "");
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
};

/**
 * Normalise a rate once the user leaves the field: round to at most 3 decimals — the same
 * precision `minorStepSize={0.001}` allows the stepper to produce — and drop any trailing
 * cruft, so "0.6555" settles to "0.656" and a half-typed "5." to "5".
 *
 * Only rewrites when the text parses to a finite number. Blank and in-between input ("", ".",
 * "-") is left exactly as typed rather than being invented into a value or silently cleared.
 */
export const roundRateForDisplay = (raw: string | undefined): string => {
  const text = raw ?? "";
  const parsed = parseFloat(text);
  if (!Number.isFinite(parsed)) return text;
  // Uses the same roundTo primitive as the money total, so the module has one rounding
  // method rather than two. (Exact decimal halfway points are float-dependent either way:
  // 0.6555 is stored just under the halfway mark, so toFixed(3) would settle it to 0.655
  // while multiply-then-round gives 0.656. Consistency matters more than that last bit.)
  // String() drops the trailing zeros a fixed-width format would add: 5.500 -> "5.5".
  return String(roundTo(parsed, RATE_PRECISION));
};

/** Guarded so a bad Long-scalar coercion upstream can never render "$NaN". */
export const formatCurrency = (value: number): string =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: CENTS_PRECISION,
    maximumFractionDigits: CENTS_PRECISION
  }).format(Number.isFinite(value) ? value : 0);

/**
 * Aggregate the planned routes into the five priceable metrics.
 *
 * **Routes with zero assigned stops are excluded** — both empty new drivers and blank existing
 * manifests (no completed, no active stops). A route with no stops drives nowhere and does no
 * work, so pricing it would bill for a driver who does nothing.
 *
 * Filtering empties here rather than only zeroing the driver count also makes the totals
 * self-consistent no matter what the map has written back yet: an emptied route's miles/transit
 * are cleared asynchronously by RoutesResultMap, so between the drag and that write-back the
 * row can still be carrying its old values.
 *
 * Hidden routes ARE included: hiddenRouteIds is a map-visibility toggle, not plan membership,
 * and a hidden route still gets created and still costs money. Unassigned jobs fall out
 * naturally — they belong to no route.
 */
export const summarizeRouteTotals = (rows: RowData[] | null | undefined): RouteCostTotals => {
  const activeRows = (rows ?? []).filter((r) => r.assignedStops.length > 0);

  let totalMiles = 0;
  let totalDurationMs = 0;
  let totalDeliveryStops = 0;
  let totalPickupStops = 0;

  for (const row of activeRows) {
    totalMiles += Number.isFinite(row.miles) ? row.miles : 0;
    totalDurationMs += Number.isFinite(row.duration) ? Math.max(0, row.duration) : 0;

    for (const stop of row.assignedStops) {
      // Skip completed stops — their cost is sunk, matches beforeTotals which excludes them
      if (stop.jobStopStatus === "C") continue;
      if (stop.stopType === JobStopType.PICKUP) {
        totalPickupStops += 1;
      } else if (stop.stopType === JobStopType.DELIVERY || stop.stopType === JobStopType.BOTH) {
        totalDeliveryStops += 1;
      }
    }
  }

  return {
    totalMiles: roundTo(totalMiles, CENTS_PRECISION),
    totalTransitSeconds: Math.round(totalDurationMs / 1000),
    totalRoutes: activeRows.length,
    totalDeliveryStops,
    totalPickupStops
  };
};

/** The quantity each rate is multiplied by. */
const multiplierFor = (key: RateKey, totals: RouteCostTotals): number => {
  switch (key) {
    case "perMile":
      return totals.totalMiles;
    case "perHour":
      return totals.totalTransitSeconds / SECONDS_PER_HOUR;
    case "perDriver":
      return totals.totalRoutes;
    case "perDeliveryStop":
      return totals.totalDeliveryStops;
    case "perPickupStop":
      return totals.totalPickupStops;
  }
};

/**
 * Total estimated cost.
 *
 * Multiplies at full rate precision and rounds ONCE at the end. Because no per-line subtotals
 * are displayed there is no visible column that has to tally, so rounding late is both simpler
 * and more accurate than summing pre-rounded lines — which would drift by up to half a cent
 * per line with the 3-decimal rates this feature supports.
 */
export const computeRouteCost = (totals: RouteCostTotals, rates: RouteCostRates): number => {
  const exact = (Object.keys(EMPTY_RATES) as RateKey[]).reduce(
    (sum, key) => sum + multiplierFor(key, totals) * parseRate(rates[key]),
    0
  );
  return roundTo(exact, CENTS_PRECISION);
};
